import React, { useState, useEffect } from "react";
import { FormControl, FormControlLabel, FormLabel, Radio, RadioGroup, TextField, Typography, Grid } from "@mui/material";
import CommonDatePicker from "../../../Base/views/CommonDatePicker";

const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
];

const formatOfferLetterDate = (val) => {
    if (!val) return "";
    const d = val instanceof Date ? val : new Date(val);
    if (isNaN(d.getTime())) return String(val);
    return `${d.getDate()} ${monthNames[d.getMonth()]} ${d.getFullYear()}`;
};

const OfferDatePickerItem = ({ field, index, initialValue, isRequired, previewError, onChange }) => {
    const parseDateVal = (val) => {
        if (!val) return null;
        const d = new Date(val);
        return d instanceof Date && !isNaN(d.getTime()) ? d : null;
    };
    const [selectedDate, setSelectedDate] = useState(() => parseDateVal(initialValue));

    useEffect(() => {
        setSelectedDate(parseDateVal(initialValue));
    }, [initialValue]);

    const handleDateChange = (value) => {
        setSelectedDate(value);
        const formatted = value && value instanceof Date && !isNaN(value.getTime())
            ? formatOfferLetterDate(value)
            : "";
        onChange(formatted);
    };

    return (
        <Grid container alignItems="center" key={field.name} spacing={2}>
            <Grid item xs={4}>
                <FormLabel sx={{ fontSize: "18px" }} required={isRequired}>
                    {`${index + 1}. ${field.label}`}
                </FormLabel>
            </Grid>
            <Grid item xs={8}>
                <CommonDatePicker
                    name={field.name}
                    size="small"
                    pickerType="date"
                    pickerFormat="dd/MM/yyyy"
                    DefaultDTvalue={selectedDate}
                    error={Boolean(isRequired && previewError)}
                    helperText={isRequired ? previewError : ""}
                    required={isRequired}
                    onKeyPress={(e) => e.preventDefault()}
                    onKeyDown={(e) => e.preventDefault()}
                    textFieldSx={{
                        width: "100%",
                        "& .MuiOutlinedInput-root": {
                            borderRadius: "4px",
                            "&:hover fieldset": {
                                borderColor: "#883bd5",
                            },
                            "&.Mui-focused fieldset": {
                                borderColor: "#883bd5",
                            },
                        },
                    }}
                    handleChange={handleDateChange}
                />
            </Grid>
        </Grid>
    );
};

export const OfferComponentsRender = ({
    setCheckData,
    setFormData,
    checkData,
    previewErrors,
    Previewvalidate,
    formFields,
    setFormComponents,
    formData
}) => {

    const handleCheckChange = (e, fieldName) => {
        const { value, checked } = e.target;
        if (checked) {
            setCheckData((prevCheckData) => ({
                values: [...prevCheckData.values, value],
            }));
        } else {
            setCheckData((prevCheckData) => ({
                values: prevCheckData.values.filter((val) => val !== value),
            }));
        }
        setFormData((prevData) => ({
            ...prevData,
            [fieldName]: checkData?.values,
        }));
    };

    const cleanKey = (val) => (val || "").toString().toLowerCase().replace(/[^a-z0-9]/g, "");
    const isAutoCtcField = (field) => {
        const name = cleanKey(field?.name);
        const label = cleanKey(field?.label);
        return (
            name === "ctc" ||
            label === "ctc" ||
            name === "ctcinwords" ||
            label === "ctcinwords" ||
            name === "ctcinword" ||
            label === "ctcinword"
        );
    };

    const handlePreviewChange = (e, type) => {
        const fieldName = e.target?.name;
        const matchingField = formFields?.find((f) => f.name === fieldName);
        if (matchingField && isAutoCtcField(matchingField)) return;
        let fieldValue;
        if (e.target?.type === 'file') {
            fieldValue = e.target?.files[0];
            setFormData((prevData) => ({
                ...prevData,
                [fieldName]: fieldValue,
            }));
        } else if (type === 'checkbox') {
            handleCheckChange(e, fieldName);
        } else {
            fieldValue = e.target?.value;
            setFormData((prevData) => ({
                ...prevData,
                [fieldName]: fieldValue,
            }));
        }
        Object.values?.(previewErrors)?.find(res => res !== "")?.length > 0 &&
            Previewvalidate(formFields, { ...formData, [fieldName]: fieldValue, });
    }

    const generatedComponents = formFields?.filter((field) => !isAutoCtcField(field))?.map((field, index) => {
        let fieldContent = null;
        switch (field.type) {
            case "text": {
                fieldContent = (
                    <Grid container alignItems="center" spacing={2}>
                        <Grid item xs={4}>
                            <FormLabel sx={{ fontSize: "18px" }} required={Boolean(field?.required)}>
                                {`${index + 1}. ${field.label}`}
                            </FormLabel>
                        </Grid>
                        <Grid item xs={8}>
                            <TextField
                                sx={{
                                    margin: "0px 0px 0px 0px",
                                    borderRadius: "4px",
                                    "& .MuiOutlinedInput-root": {
                                        "& fieldset": {
                                            borderColor: "transparent",
                                        },
                                        "&:hover fieldset": {
                                            borderColor: "#883bd5",
                                            borderBottomWidth: 2,
                                            borderBottomColor: "#883bd5",
                                            borderTopWidth: 0,
                                            borderLeftWidth: 0,
                                            borderRightWidth: 0,
                                        },
                                    },
                                }}
                                InputLabelProps={{
                                    sx: {
                                        marginLeft: "5px",
                                    }
                                }}
                                placeholder="Enter your text"
                                name={field.name}
                                fullWidth
                                multiline
                                variant="standard"
                                margin="normal"
                                size="small"
                                required={Boolean(field?.required)}
                                value={formData?.[field.name] !== undefined ? formData[field.name] : ""}
                                onChange={handlePreviewChange}
                                {...field && field?.required && previewErrors?.[field?.name] && {
                                    error: true,
                                    helperText: previewErrors[field?.name],
                                }}
                            />
                        </Grid>
                    </Grid>
                );
                break;
            }
            case "date":
                fieldContent = (
                    <OfferDatePickerItem
                        key={field.name}
                        field={field}
                        index={index}
                        initialValue={formData?.[field.name]}
                        isRequired={Boolean(field?.required)}
                        previewError={previewErrors?.[field?.name]}
                        onChange={(formatted) => {
                            handlePreviewChange({
                                target: {
                                    name: field.name,
                                    value: formatted,
                                }
                            });
                        }}
                    />
                );
                break;
            case "radio":
                fieldContent = (
                    <Grid container alignItems="center" spacing={2}>
                        <Grid item xs={4}>
                            <FormLabel sx={{ fontSize: "18px" }} required={field?.required}>
                                {`${index + 1}. ${field.label}`}
                            </FormLabel>
                        </Grid>
                        <Grid item xs={8}>
                            <FormControl component="fieldset" margin="normal">
                                <RadioGroup
                                    value={formData?.[field.name] || ""}
                                    name={field.name}
                                    onChange={handlePreviewChange}
                                >
                                    {field?.options &&
                                        field?.options.map((option, optionIndex) => (
                                            <FormControlLabel
                                                sx={{ margin: "0px 0px 0px 0px" }}
                                                key={option.value}
                                                value={option.value}
                                                control={<Radio />}
                                                label={option.label}
                                            />
                                        ))}
                                </RadioGroup>
                                {previewErrors?.[field.name] && (
                                    <Typography variant="body2" color="error">
                                        {previewErrors[field.name]}
                                    </Typography>
                                )}
                            </FormControl>
                        </Grid>
                    </Grid>
                );
                break;
            case "file":
                fieldContent = (
                    <Grid container alignItems="center" spacing={2}>
                        <Grid item xs={4}>
                            <FormLabel sx={{ fontSize: "18px" }} required={field?.required}>
                                {`${index + 1}. ${field.label}`}
                            </FormLabel>
                        </Grid>
                        <Grid item xs={8}>
                            <TextField
                                sx={{ margin: "0px 0px 0px 0px", borderRadius: "4px" }}
                                name={field.name}
                                size="small"
                                required={field?.required}
                                fullWidth
                                variant="outlined"
                                margin="normal"
                                type="file"
                                onChange={handlePreviewChange}
                                {...field && field?.required && previewErrors?.[field?.name] && {
                                    error: true,
                                    helperText: previewErrors[field?.name],
                                }}
                            />
                        </Grid>
                    </Grid>
                );
                break;
            default:
                return null;
        }

        return (
            <Grid
                item
                key={field.name}
                xs={12}
                sm={12}
                md={12}
                lg={12}
                sx={{ padding: "1.5rem 1.25rem" }}
            >
                {fieldContent}
            </Grid>
        );
    });

    if (typeof setFormComponents === "function") {
        setFormComponents(generatedComponents);
    }
    return <>{generatedComponents}</>;
};
